/**
 * Report adapters — the only place that knows what a bot puts in its summary.
 * ---------------------------------------------------------------------------
 *
 * The four analysis bots do NOT share a summary shape. Today:
 *
 *   tern   (botox)      overallSymmetry, lowerSide, regions{}, topZone, interest[]
 *   lynx   (lift)       youthfulness, overallLaxity, triangleIndex, balance,
 *                       descendedSide, topZone, priorities[]
 *   serval (filler)     harmony, balance, lipRatio, needsVolumeSide,
 *                       topZone, priorities[]
 *   swift  (rhino)      harmony, nasofrontal, nasolabial, dorsalShape,
 *                       tipProjection, goodeLeft/Right/Average, outsideRange[]
 *
 * Only `measures` and `poseCount` are common, and the headline score lives
 * under four different names. So the UI never reads a bot's keys directly:
 * each bot gets a descriptor here that maps its own summary onto one fixed
 * view-model, and the components only ever see that view-model.
 *
 * Changing a bot — or adding a fifth — means editing ONE descriptor in this
 * file. No component changes, no new props, nothing to hunt for.
 *
 * The view-model:
 *
 *   {
 *     headline: { value, max, unit, label } | null   the one figure to lead with
 *     stats:    [{ label, value, unit }] | [{ label, text }]
 *                                                   secondary figures, in order.
 *                                                   `value` is a NUMBER so the
 *                                                   view can render it in the
 *                                                   reader's own numerals;
 *                                                   `text` is already-worded.
 *     zones:    [string]                             areas the bot flagged
 *     poseCount: number | null
 *   }
 *
 * Every field is optional in practice: a bot that stops emitting something
 * drops that row rather than breaking the page.
 */

/** Round without inventing precision the bot did not provide.
 *
 *  Returns a NUMBER, never a formatted string: the digits a reader sees
 *  depend on their language (Persian numerals in a Persian page), and that is
 *  the view's job. Formatting here hard-codes Latin digits into every locale.
 */
function num(v, digits = 0) {
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  return Number(v.toFixed(digits));
}

/** A side ('left' | 'right' | anything else) as a translation key suffix. */
function side(v) {
  return v === 'left' || v === 'right' ? v : 'balanced';
}

/**
 * What each bot's landmark numbers MEAN, so the drawing can be coloured.
 * ---------------------------------------------------------------------
 *
 * The measure map a bot records is geometry only — point indices and the lines
 * between them. It does not say that point 263 is an eye corner, or that the
 * three lines of the facial-thirds measure are the upper, middle and lower
 * third. Without that, every measurement can only be drawn in one colour.
 *
 * So each bot declares it here, alongside its summary reader, and this stays
 * the only file in the app that knows anything bot-specific. Every field is
 * optional and degrades on its own (see `measurePaint.js`):
 *
 *   region          landmark index → the area of the face the bot groups it
 *                   under. Taken from the bot's own PAIRS / landmark tables.
 *   regionOrder     the areas in a fixed order, so an area keeps the same
 *                   colour on every step of the report instead of changing
 *                   colour with each measure.
 *   regionScoreKey  the summary field holding a score per area, on the 0–100
 *                   scale the report colours — Tern's `regions`.
 *   scoreKeys       summary fields that are 0–100 and higher-is-better, so a
 *                   measure naming one in its `summaryKey` can be graded.
 *                   Degrees and ratios are deliberately NOT listed: there is
 *                   no good or bad end of a nasolabial angle.
 *   segments        measures whose lines are separate, ordered quantities,
 *                   named in line order.
 *   focus           measures that name more points than they are read around:
 *                   the areas the measurement is actually about. Mirrors the
 *                   bot's own group definitions.
 *
 * A bot that changes its landmark table needs its entry here updated, and the
 * mismatch is visible rather than silent: an index that is no longer in the map
 * is drawn plain, and a measure whose areas are unknown falls back to one
 * colour. No component changes either way.
 */

/** Tern's symmetric pairs, by area — its `PAIRS` table, which is also what the
 *  symmetry and muscle measures are built from. */
const TERN_REGIONS = {
  263: 'eyes', 33: 'eyes', 362: 'eyes', 133: 'eyes',
  386: 'eyes', 159: 'eyes', 374: 'eyes', 145: 'eyes',
  300: 'brows', 70: 'brows', 334: 'brows', 105: 'brows', 336: 'brows', 107: 'brows',
  294: 'nose', 64: 'nose',
  291: 'mouth', 61: 'mouth', 409: 'mouth', 185: 'mouth', 375: 'mouth', 146: 'mouth',
  280: 'cheeks', 50: 'cheeks',
  454: 'jaw', 234: 'jaw', 397: 'jaw', 172: 'jaw',
  356: 'temples', 127: 'temples',
};

/** Which area each muscle group is read around — Tern's `MUSCLE_GROUPS`. The
 *  measure map gives every muscle the full paired-point set, so without this
 *  all eleven muscle steps would draw the same thirty points. */
const TERN_MUSCLE_FOCUS = {
  'muscle:frontalis': ['brows'],
  'muscle:glabella': ['brows'],
  'muscle:orbicularis_oculi': ['eyes'],
  'muscle:nasalis': ['nose'],
  'muscle:zygomaticus': ['mouth', 'cheeks'],
  'muscle:orbicularis_oris': ['mouth'],
  'muscle:dao': ['mouth'],
  'muscle:levator_labii': ['mouth'],
  'muscle:risorius': ['mouth'],
  'muscle:dli': ['mouth'],
  'muscle:mentalis': ['mouth'],
};

/**
 * One descriptor per bot.
 *
 * `key` is matched against the analysis's agent_key, case-insensitively and
 * on a prefix, because the deployed agent folders are named "Tern Plus",
 * "Lynx Plus" and so on while the catalog keys are short.
 */
const ADAPTERS = [
  {
    key: 'tern',
    /** Botox planning: how evenly the face moves. */
    read: (m, t) => ({
      headline: m.overallSymmetry == null ? null : {
        value: num(m.overallSymmetry, 0), max: 100, unit: '%',
        label: t('candleCard.metric.symmetry'),
      },
      stats: [
        m.lowerSide ? {
          label: t('candleCard.metric.lowerSide'),
          text: t(`candleCard.side.${side(m.lowerSide)}`),
        } : null,
        Array.isArray(m.interest) && m.interest.length ? {
          label: t('candleCard.metric.activeZones'),
          value: m.interest.length,
        } : null,
      ].filter(Boolean),
      zones: Array.isArray(m.interest) ? m.interest : [],
    }),
    paint: {
      region: TERN_REGIONS,
      regionOrder: ['brows', 'temples', 'eyes', 'nose', 'cheeks', 'mouth', 'jaw'],
      // The symmetry measure is drawn with one colour per area, taken from the
      // score the report prints for that area. This is the colour map.
      regionScoreKey: 'regions',
      scoreKeys: ['overallSymmetry'],
      // Trichion → glabella → subnasale → menton: three thirds, three readings.
      segments: { thirds: ['upper', 'middle', 'lower'] },
      focus: TERN_MUSCLE_FOCUS,
    },
  },
  {
    key: 'lynx',
    /** Lift planning: skin laxity and the youth triangle. */
    read: (m, t) => ({
      headline: m.youthfulness == null ? null : {
        value: num(m.youthfulness, 0), max: 100, unit: '',
        label: t('candleCard.metric.youthfulness'),
      },
      stats: [
        m.overallLaxity == null ? null : {
          label: t('candleCard.metric.laxity'), value: num(m.overallLaxity, 0),
        },
        m.triangleIndex == null ? null : {
          label: t('candleCard.metric.triangleIndex'), value: num(m.triangleIndex, 0),
        },
        m.balance == null ? null : {
          label: t('candleCard.metric.balance'), value: num(m.balance, 0), unit: '%',
        },
      ].filter(Boolean),
      zones: Array.isArray(m.priorities) ? m.priorities : [],
    }),
    paint: {
      region: {
        334: 'brows', 105: 'brows',
        386: 'eyes', 159: 'eyes', 263: 'eyes', 33: 'eyes',
        280: 'cheeks', 50: 'cheeks', 345: 'cheeks', 116: 'cheeks',
        358: 'nasolabial', 423: 'nasolabial', 426: 'nasolabial', 436: 'nasolabial',
        129: 'nasolabial', 203: 'nasolabial', 206: 'nasolabial', 216: 'nasolabial',
        291: 'mouth', 61: 'mouth', 13: 'mouth', 14: 'mouth',
        172: 'jaw', 397: 'jaw',
        152: 'chin',
      },
      regionOrder: ['brows', 'eyes', 'cheeks', 'nasolabial', 'mouth', 'jaw', 'chin'],
      // The triangle-of-youth measure names `triangleIndex`, which is a 0–100
      // score, so that step is graded. Nothing else here is a score.
      scoreKeys: ['youthfulness', 'triangleIndex', 'balance'],
    },
  },
  {
    key: 'serval',
    /** Filler planning: proportion between the facial thirds, and the lips. */
    read: (m, t) => ({
      headline: m.harmony == null ? null : {
        value: num(m.harmony, 0), max: 100, unit: '',
        label: t('candleCard.metric.harmony'),
      },
      stats: [
        m.balance == null ? null : {
          label: t('candleCard.metric.balance'), value: num(m.balance, 0), unit: '%',
        },
        m.lipRatio == null ? null : {
          label: t('candleCard.metric.lipRatio'), value: num(m.lipRatio, 2),
        },
      ].filter(Boolean),
      zones: Array.isArray(m.priorities) ? m.priorities : [],
    }),
    paint: {
      region: {
        300: 'brows',
        374: 'tearTrough', 347: 'tearTrough', 145: 'tearTrough', 118: 'tearTrough',
        263: 'eyes', 33: 'eyes',
        356: 'temples', 127: 'temples',
        280: 'cheeks', 50: 'cheeks',
        358: 'nasolabial', 423: 'nasolabial', 426: 'nasolabial', 436: 'nasolabial',
        129: 'nasolabial', 203: 'nasolabial', 206: 'nasolabial', 216: 'nasolabial',
        0: 'lips', 13: 'lips', 14: 'lips', 17: 'lips',
        454: 'jaw', 234: 'jaw', 397: 'jaw', 172: 'jaw',
      },
      regionOrder: ['brows', 'eyes', 'temples', 'cheeks', 'tearTrough',
        'nasolabial', 'lips', 'jaw'],
      // `lipRatio` is a proportion, not a score — there is no good or bad end
      // of it — so the lip measure is grouped by area rather than graded.
      scoreKeys: ['harmony', 'balance'],
    },
  },
  {
    key: 'swift',
    /** Rhinoplasty planning: the profile angles. */
    read: (m, t) => ({
      headline: m.harmony == null ? null : {
        value: num(m.harmony, 0), max: 100, unit: '',
        label: t('candleCard.metric.harmony'),
      },
      stats: [
        m.nasofrontal == null ? null : {
          label: t('candleCard.metric.nasofrontal'), value: num(m.nasofrontal, 0), unit: '°',
        },
        m.nasolabial == null ? null : {
          label: t('candleCard.metric.nasolabial'), value: num(m.nasolabial, 0), unit: '°',
        },
        m.goodeAverage == null ? null : {
          label: t('candleCard.metric.goode'), value: num(m.goodeAverage, 2),
        },
      ].filter(Boolean),
      // This bot names what sits outside the usual range rather than zones of
      // interest; for the client it reads the same way — what to talk about.
      zones: Array.isArray(m.outsideRange) ? m.outsideRange : [],
    }),
    paint: {
      // Every nasal measure is an angle or a projection between two or three
      // points, so there is nothing to grade and no second area to group by —
      // except the landmarks OFF the nose that each angle is measured against.
      // Separating those two is the one thing worth colouring here: this is the
      // nose, and this is what it is being compared to.
      region: {
        168: 'nose', 197: 'nose', 195: 'nose', 4: 'nose',
        94: 'nose', 64: 'nose', 294: 'nose', 2: 'nose',
        9: 'reference', 0: 'reference', 152: 'reference',
        133: 'reference', 362: 'reference',
      },
      regionOrder: ['nose', 'reference'],
      scoreKeys: ['harmony'],
    },
  },
];

/**
 * The fallback for a bot nobody has written a descriptor for yet.
 *
 * It exists so a new agent — or a renamed one — still renders something true
 * on the day it ships, instead of an empty card that looks like a bug. It
 * takes the first plausible 0–100 score it can find for the headline and shows
 * the remaining numbers as plain stats, labelled with the bot's own key names.
 *
 * Anything it produces is a prompt to write a real descriptor above: only the
 * descriptor knows that `lipRatio` is a ratio and `nasofrontal` is degrees.
 */
const SCORE_KEYS = ['overallSymmetry', 'youthfulness', 'harmony', 'score', 'overall'];

function genericRead(m) {
  const headlineKey = SCORE_KEYS.find((k) => typeof m[k] === 'number');
  const skip = new Set([...SCORE_KEYS, 'measures', 'poseCount']);
  const stats = Object.entries(m)
    .filter(([k, v]) => !skip.has(k) && typeof v === 'number' && Number.isFinite(v))
    .slice(0, 4)
    .map(([k, v]) => ({ label: k, value: num(v, 2) }));
  const zones = [m.topZone, ...(m.interest || []), ...(m.priorities || []),
    ...(m.outsideRange || [])].filter((z) => typeof z === 'string');
  return {
    headline: headlineKey == null ? null : {
      value: num(m[headlineKey], 0), max: 100, unit: '', label: headlineKey,
    },
    stats,
    zones: [...new Set(zones)],
  };
}

/**
 * Turn one report's raw agent summary into what the UI draws.
 *
 * @param {{agentKey?: string, metrics?: object}} report
 * @param {(key: string, opts?: object) => string} t  i18next translator
 * @returns {{headline: object|null, stats: Array, zones: string[],
 *            poseCount: number|null, adapted: boolean}}
 */
export function readReport(report, t) {
  const m = report?.metrics;
  if (!m || typeof m !== 'object') {
    return { headline: null, stats: [], zones: [], poseCount: null, adapted: false };
  }
  const key = String(report.agentKey || '').toLowerCase();
  const adapter = ADAPTERS.find((a) => key.startsWith(a.key));
  const view = adapter ? adapter.read(m, t) : genericRead(m);
  return {
    headline: view.headline ?? null,
    stats: view.stats ?? [],
    // Zones come from the bots in English; they are anatomical names, which
    // is also how clinicians say them in Persian conversation.
    zones: (view.zones ?? []).filter(Boolean).slice(0, 4),
    poseCount: typeof m.poseCount === 'number' ? m.poseCount : null,
    adapted: Boolean(adapter),
  };
}

/**
 * What a bot's landmark numbers mean, for the drawing layer.
 *
 * Returns null for a bot nobody has described yet — which is not a failure:
 * `measurePaint` then draws every point in one colour, which is all anyone can
 * honestly say about points whose meaning is unknown.
 *
 * @param {string} agentKey  the analysis's agent key, e.g. "Tern Plus"
 */
export function paintFor(agentKey) {
  const key = String(agentKey || '').toLowerCase();
  return ADAPTERS.find((a) => key.startsWith(a.key))?.paint ?? null;
}

/** The analysis families the card collects, in the order they are shown. */
export const ANALYSIS_FAMILIES = [
  { key: 'tern', labelKey: 'candleCard.family.tern' },
  { key: 'serval', labelKey: 'candleCard.family.serval' },
  { key: 'lynx', labelKey: 'candleCard.family.lynx' },
  { key: 'swift', labelKey: 'candleCard.family.swift' },
];

/** Which family a report belongs to, or null for an unrecognised bot. */
export function familyOf(agentKey) {
  const key = String(agentKey || '').toLowerCase();
  return ANALYSIS_FAMILIES.find((f) => key.startsWith(f.key))?.key ?? null;
}

export default readReport;
